/**
 * The bridge between Phira's manifest (info.yml / info.txt / info.csv, `InfoYml`) and the editor document
 * (W1-07b formats-package; formats-and-packaging.md P§2.3, P§6.1 steps 7 and 9, P§6.2; DESIGN.md §7.2, §7.3).
 *
 * Import (`applyManifest`): descriptive fields, preview range, tags, intro and tip go to `meta`; the play-field
 * switches, the extra offset, unknown keys and the server fields go to `pkg`; values Phira's model cannot hold are
 * repaired and reported ('changed': an aspect ratio or line length that is not a positive number, a background
 * dim outside 0..1, a non-finite difficulty or offset). Every number is judged as the f32 Phira reads: `1e-50`
 * is an aspect ratio of 0 and `1e39` an infinite offset there, while a dim of `1.00000001` is 1 (taken as 1 without
 * a report). `useAttachUiFix: null` becomes true as Phira's importer sets it ('normalized' when it matters: an
 * explicit info.yml or attachUI lines). `clampPreview` moves a preview range that lies outside the song (Phira
 * refuses to open such a chart; a bound beyond the f32 range counts as outside) inside it ('changed').
 *
 * Export (`infoFromDoc`): every ChartInfo field from the document, chart / music / illustration / unlockVideo as the
 * package paths written, `format: rpe`, both switches explicit, `offset` = the sub-millisecond remainder or imported
 * tweak, server fields only when the project was imported with them, unknown imported keys kept.
 */

import type { ChartDoc, ChartMeta, PackageSettings, ServerFields } from '../../core/model/types';
import { PREVIEW_LENGTH_SEC } from '../../core/model/defaults';
import type { RpeSpeedMode } from '../rpe/normalize';
import type { InfoYml, ManifestResult } from '../info/types';
import type { ReportSink } from './report';

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

/** Margin kept between the preview end and the end of the song (decoders disagree on MP3 padding). */
export const PREVIEW_MARGIN_SEC = 0.5;

export interface ManifestAssets {
  readonly music: ChartDoc['meta']['music'];
  readonly illustration: ChartDoc['meta']['illustration'];
  readonly unlockVideo: ChartDoc['meta']['unlockVideo'];
}

export interface ApplyManifestOptions {
  readonly assets: ManifestAssets;
  /** Sub-millisecond offset remainder of a PGR / PEC import (seconds), added to info.yml `offset`. */
  readonly extraOffsetSec: number;
  readonly speedMode: RpeSpeedMode;
  /** The chart has attachUI lines (useAttachUiFix matters). */
  readonly hasAttachUi: boolean;
  /**
   * The document was restored from phiedit.json: only an info.yml overrides it (Phira may have edited info.yml, e.g.
   * its offset tweak); legacy manifests and inferred values never do.
   */
  readonly restored: boolean;
  /**
   * The manifest could not be read as a whole (`resolveManifestDetailed` readable false): its values are Phira's
   * defaults and META inference, so it never overrides a restored document.
   */
  readonly unreadable?: boolean;
}

/** Whether Phira reads `v` as a finite f32 (`1e39` overflows to infinity there). */
function finiteF32(v: number): boolean {
  return Number.isFinite(Math.fround(v));
}

function finiteOr(v: number, fallback: number): number {
  return finiteF32(v) ? v : fallback;
}

/** A positive number that is also positive and finite as Phira's f32 (`1e-50` reads as 0 there). */
function positiveF32(v: number): boolean {
  const f = Math.fround(v);
  return f > 0 && Number.isFinite(f);
}

/**
 * An f32 manifest value applied to a document field: the document's own value is kept when it is the same f32
 * (info.yml stores `1.7777778` for 16/9; a restored project keeps its exact value). `-0.0` is taken as 0 (Phira
 * makes no difference, and exports write 0).
 */
function f32Field(current: number, manifest: number): number {
  if (Math.fround(current) === Math.fround(manifest)) return current;
  return manifest === 0 ? 0 : manifest;
}

function f32Nullable(current: number | null, manifest: number | null): number | null {
  return current !== null && manifest !== null ? f32Field(current, manifest) : manifest;
}

function serverOf(info: InfoYml): ServerFields | null {
  const out: Mutable<ServerFields> = {};
  if (info.id !== null) out.id = info.id;
  if (info.uploader !== null) out.uploader = info.uploader;
  if (info.created !== null) out.created = info.created;
  if (info.updated !== null) out.updated = info.updated;
  if (info.chartUpdated !== null) out.chartUpdated = info.chartUpdated;
  return Object.keys(out).length === 0 ? null : out;
}

/** Applies a resolved manifest to an imported document (see the module header). */
export function applyManifest(
  doc: ChartDoc,
  manifest: ManifestResult,
  opts: ApplyManifestOptions,
  r: ReportSink,
): ChartDoc {
  if (opts.restored && (manifest.source !== 'yml' || opts.unreadable === true)) {
    return { ...doc, meta: { ...doc.meta, ...opts.assets } };
  }
  const info = manifest.info;
  const meta: Mutable<ChartMeta> = {
    ...doc.meta,
    name: info.name,
    level: info.level,
    charter: info.charter,
    composer: info.composer,
    illustrator: info.illustrator,
    difficulty: f32Field(doc.meta.difficulty, info.difficulty),
    previewStart: f32Field(doc.meta.previewStart, info.previewStart),
    previewEnd: f32Nullable(doc.meta.previewEnd, info.previewEnd),
    tags: [...info.tags],
    intro: info.intro,
    tip: info.tip,
    ...opts.assets,
  };
  if (!finiteF32(info.difficulty)) {
    meta.difficulty = 10;
    r.add('changed', 'pkg.report.difficultyInvalid', { value: String(info.difficulty) }, 'info.yml difficulty');
  }
  const pkg: Mutable<PackageSettings> = {
    ...doc.pkg,
    aspectRatio: f32Field(doc.pkg.aspectRatio, info.aspectRatio),
    forceAspectRatio: info.forceAspectRatio,
    backgroundDim: f32Field(doc.pkg.backgroundDim, info.backgroundDim),
    lineLength: f32Field(doc.pkg.lineLength, info.lineLength),
    holdPartialCover: info.holdPartialCover,
    noteUniformScale: info.noteUniformScale,
    useAttachUiFix: info.useAttachUiFix ?? true,
    speedMode: opts.speedMode,
    infoOffsetSec: f32Field(doc.pkg.infoOffsetSec, finiteOr(info.offset, 0) + opts.extraOffsetSec),
    infoExtra: info.extra,
    server: serverOf(info),
  };
  if (!positiveF32(info.aspectRatio)) {
    pkg.aspectRatio = 16 / 9;
    r.add('changed', 'pkg.report.aspectRatioInvalid', { value: String(info.aspectRatio) }, 'aspectRatio');
  }
  if (!positiveF32(info.lineLength)) {
    pkg.lineLength = 6;
    r.add('changed', 'pkg.report.lineLengthInvalid', { value: String(info.lineLength) }, 'lineLength');
  }
  const dim32 = Math.fround(info.backgroundDim);
  if (!(dim32 >= 0 && dim32 <= 1)) {
    pkg.backgroundDim = Number.isNaN(info.backgroundDim) ? 0.6 : Math.min(1, Math.max(0, info.backgroundDim));
    r.add('changed', 'pkg.report.backgroundDimClamped', { value: String(info.backgroundDim) }, 'backgroundDim');
  } else if (!(pkg.backgroundDim >= 0 && pkg.backgroundDim <= 1)) {
    // Outside 0..1 only before rounding to f32 (1.00000001, -1e-50): Phira reads the bound itself.
    pkg.backgroundDim = dim32 === 0 ? 0 : 1;
  }
  if (!finiteF32(info.offset)) {
    r.add('changed', 'pkg.report.offsetInvalid', { value: String(info.offset) }, 'offset');
  }
  if (info.useAttachUiFix === null && (manifest.source === 'yml' || opts.hasAttachUi)) {
    r.add('normalized', 'pkg.report.attachUiFixDefault', undefined, 'useAttachUiFix');
  }
  return { ...doc, meta, pkg };
}

/**
 * Moves a preview range Phira would refuse inside the song: Phira refuses to open a chart whose
 * `previewEnd ?? previewStart + 1` exceeds the decoded music length and panics when start > end; the model also
 * needs a finite start >= 0 and end > start. A bound beyond the f32 range (Phira reads it as infinite) is reset like
 * a non-finite one. `durationSec` null = unknown (only the ordering is repaired). The range ends 0.5 s before the
 * song does, or halfway through a song shorter than that (no one-second default fits it), so the result is always
 * one this function accepts again; null when nothing changes.
 */
export function clampPreview(
  meta: ChartMeta,
  durationSec: number | null,
): { start: number; end: number | null } | null {
  let start = meta.previewStart;
  let end = meta.previewEnd;
  let changed = false;
  if (!(finiteF32(start) && start >= 0)) {
    start = 0;
    changed = true;
  }
  if (end !== null && !(finiteF32(end) && end > start)) {
    end = null;
    changed = true;
  }
  if (durationSec !== null && Number.isFinite(durationSec) && durationSec > 0) {
    const loadEnd = end ?? start + 1;
    if (loadEnd > durationSec) {
      const limit = Math.max(0, durationSec - PREVIEW_MARGIN_SEC);
      const length = Math.min(Math.max((end ?? start + PREVIEW_LENGTH_SEC) - start, 1), PREVIEW_LENGTH_SEC);
      if (limit <= 0) {
        start = 0;
        end = durationSec / 2;
      } else {
        end = limit;
        start = Math.max(0, Math.min(start, limit - length));
        if (!(end > start)) end = null;
      }
      changed = true;
    }
  }
  return changed && (start !== meta.previewStart || end !== meta.previewEnd) ? { start, end } : null;
}

export interface ExportNames {
  readonly chart: string;
  readonly music: string;
  readonly illustration: string;
  readonly unlockVideo: string | null;
}

/** The info.yml an export writes for a document (see the module header). */
export function infoFromDoc(doc: ChartDoc, names: ExportNames, speedMode: RpeSpeedMode): InfoYml {
  const { meta, pkg } = doc;
  const server = pkg.server;
  return {
    id: server?.id ?? null,
    uploader: server?.uploader ?? null,
    name: meta.name,
    difficulty: meta.difficulty,
    level: meta.level,
    charter: meta.charter,
    composer: meta.composer,
    illustrator: meta.illustrator,
    chart: names.chart,
    format: 'rpe',
    music: names.music,
    illustration: names.illustration,
    unlockVideo: names.unlockVideo,
    previewStart: meta.previewStart,
    previewEnd: meta.previewEnd,
    aspectRatio: pkg.aspectRatio,
    backgroundDim: pkg.backgroundDim,
    lineLength: pkg.lineLength,
    offset: pkg.infoOffsetSec,
    tip: meta.tip,
    tags: meta.tags,
    intro: meta.intro,
    holdPartialCover: pkg.holdPartialCover,
    noteUniformScale: pkg.noteUniformScale,
    forceAspectRatio: pkg.forceAspectRatio,
    useRpe170Speed: speedMode === 'rpe170',
    useAttachUiFix: pkg.useAttachUiFix,
    created: server?.created ?? null,
    updated: server?.updated ?? null,
    chartUpdated: server?.chartUpdated ?? null,
    extra: pkg.infoExtra,
  };
}
